import { OUTBRAIN_CSS_SELECTOR, CLICK_BANNER_LOCAL_STORAGE_KEY } from '../const/common';
import Event from '../const/event';
import { log } from '../utils/debug';
import { isOutbrainWidgetInViewport } from '../utils/outbrain';
import { carouselStyle, createCarousel } from './utils';

require('./webBanner.scss');

const CONTENT_WIDTH_DEFAULT = 600;
const TRACK_EVENTS = {
  DELIVERED: 'delivered',
  CLICKED: 'clicked',
  CLOSED: 'closed',
  BOUNCED: 'bounced',
  CONVERSIONS: 'conversions'
};
const WEB_BANNER_OPEN_CLOSE_CSS_CLASS = 'cleverpush-banner-open';
const TYPE_BUTTON = 'button';
const CLOSE_BANNER_BUTTON_ACTION = '#close';
const ROW_ID_PREFIX = 'id*=row';
const ELEMENT_ID_PREFIX = 'id*=element';
const WEB_BANNER_BUTTON_ACTIONS = {
  JAVASCRIPT_FUNCTION: 'function',
  HIDE_ROW: 'hideRow',
  SHOW_ROW: 'showRow',
  HIDE_ELEMENT: 'hideElement',
  SHOW_ELEMENT: 'showElement'
};

export default class WebBanner {
  constructor(webBanner, context) {
    const {
      config, api, trigger
    } = context;
    this.context = context;

    this.webBanner = webBanner;
    this.config = config;
    this.api = api;
    this.trigger = trigger;
    this.element = null;
    this.isVisible = false;

    const isMobile = window.innerWidth < 768;
    if (isMobile && this.webBanner.mobileStyle) {
      this.webBanner.style = { ...this.webBanner.style, ...this.webBanner.mobileStyle };
    }

    this.contentWidth = Math.min(this.webBanner.style.width || CONTENT_WIDTH_DEFAULT, window.innerWidth);
    this.previousHeight = 0;
    if (webBanner.testId) {
      this.testId = webBanner.testId;
    }
  }

  setIframeHeight(height) {
    if (!this.iframe || this.iframe.style.height === '100%') {
      // could be set from story
      return;
    }

    if (height) {
      this.iframe.style.height = height;
      return;
    }

    if (this.iframe.contentWindow.document.body.scrollHeight !== this.previousHeight) {
      this.iframe.style.height = `${this.iframe.contentWindow.document.body.scrollHeight}px`;
      this.previousHeight = this.iframe.contentWindow.document.body.scrollHeight;
      if (this.iframe.contentWindow.document.body.scrollHeight > window.screen.height) {
        this.content.style.maxHeight = '100%';
      }
    }
  }

  setupIframe = (contentWindow) => {
    const { document } = contentWindow;
    const bannerId = this.webBanner._id;
    const isPreview = this.config.env === 'PREVIEW';
    const testId = this.testId;
    const oncePerClickOnly = this.webBanner.oncePerClick;
    const closeBanners = () => {
      for (let bannerIndex = 0; bannerIndex < this.context.webBanners.length; bannerIndex += 1) {
        if (this.context.webBanners[bannerIndex].element) {
          this.context.webBanners[bannerIndex].element.style.display = 'none';
          top.document.body.classList.remove(WEB_BANNER_OPEN_CLOSE_CSS_CLASS);
          this.context.webBanners[bannerIndex].removeBounceListener();
        }
      }
    };
    contentWindow.closeBanners = closeBanners;

    const openStory = (url) => {
      for (let bannerIndex = 0; bannerIndex < this.context.webBanners.length; bannerIndex += 1) {
        if (this.context.webBanners[bannerIndex].element) {
          const content = this.context.webBanners[bannerIndex].element.querySelector('.banner-content');
          if (content) {
            content.style.top = 0;
            content.style.right = 0;
            content.style.bottom = 0;
            content.style.left = 0;
            if (window.innerWidth < 768) {
              content.style.paddingTop = 0;
              content.style.paddingBottom = 0;
            }
            this.context.webBanners[bannerIndex].iframe.style.height = '100%';
          }
        }
      }

      document.body.style.display = 'flex';
      document.body.style.alignItems = 'center';
      document.body.style.justifyContent = 'center';

      const mainTable = document.querySelector('body > table');
      if (mainTable) {
        mainTable.parentNode.removeChild(mainTable);
      }

      const script = document.createElement('script');
      const link = document.createElement('link');
      script.src = 'https://cdn.ampproject.org/amp-story-player-v0.js';
      script.async = true;
      script.onload = function () {
        const ASPECT_RATIO = 0.6;
        const playerEl = document.createElement('amp-story-player');
        const height = 600;
        const width = `${height * ASPECT_RATIO}px`;

        if (window.innerWidth < 768) {
          playerEl.style.width = '100%';
          playerEl.style.height = '100%';
        } else {
          playerEl.style.width = width;
          playerEl.style.maxWidth = '100%';
          playerEl.style.height = `${height}px`;
        }
        playerEl.style.margin = '0 auto';

        document.body.appendChild(playerEl);

        const a = document.createElement('a');
        a.setAttribute('href', url);
        playerEl.appendChild(a);

        // eslint-disable-next-line no-undef
        const player = new AmpStoryPlayer(window, playerEl);
        setTimeout(() => {
          player.load();
        }, 0);
      };
      link.href = 'https://cdn.ampproject.org/amp-story-player-v0.css';
      link.rel = 'stylesheet';
      link.type = 'text/css';
      document.head.append(script, link);
    };

    const bannerCloseLinks = document.querySelectorAll('a[href="#close"]');
    for (let i = 0; i < bannerCloseLinks.length; i += 1) {
      bannerCloseLinks[i].addEventListener('click', (e) => {
        e.preventDefault();
        closeBanners();
      });
    }

    const bannerDoNothingLinks = document.querySelectorAll('a[href="#"]');
    for (let i = 0; i < bannerDoNothingLinks.length; i += 1) {
      bannerDoNothingLinks[i].addEventListener('click', (e) => {
        e.preventDefault();
      });
    }

    const openStoryLinks = document.querySelectorAll('a[href*="#open-story"]');
    for (let i = 0; i < openStoryLinks.length; i += 1) {
      openStoryLinks[i].addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.currentTarget) {
          openStory(e.currentTarget.href.split('#')[0]);
        }
      });
    }

    function showElement(elementId) {
      const element = document.querySelector(elementId);
      if (element) {
        element.style.display = 'block';
      }
    }

    function hideElement(elementId) {
      const element = document.querySelector(elementId);
      if (element) {
        document.body.style.setProperty('height', `${document.body.scrollHeight - element.clientHeight}px`, 'important');
        element.style.display = 'none';
      }
    }

    const bannerButtons = document.querySelectorAll('a, button, input[type="submit"], input[type="button"]');
    const clickedElementIds = {};

    for (let i = 0; i < bannerButtons.length; i += 1) {
      // eslint-disable-next-line no-loop-func
      bannerButtons[i].addEventListener('click', (e) => {
        if (e.currentTarget && e.currentTarget.href && e.currentTarget.href.indexOf('#open-story') >= 0) {
          return;
        }

        let elementId = e.currentTarget.id.split('element-')[1];

        if (!elementId) {
          const buttonElement = e.currentTarget.closest('[id*="element-"]');
          if (buttonElement && buttonElement.id) {
            elementId = buttonElement.id.split('element-')[1];
          }

          const parentElement = e.target.parentElement;
          if (!elementId && parentElement && parentElement.id) {
            elementId = parentElement.id.split('-')[1];
          }
        }

        if (oncePerClickOnly) {
          this.saveBannerClick(bannerId);
        }
        let webBannerButtons = [];
        (this.webBanner?.contentState?.rows || []).forEach((row) => {
          (row.cols || []).forEach((col) => {
            const buttons = (col.elements || []).filter((webBannerButton) => webBannerButton.type === 'button' || webBannerButton.type === 'image' || webBannerButton.type === 'html');
            webBannerButtons = webBannerButtons.concat(buttons);

            (col.subRowCols || []).forEach((subRowCol) => {
              const subButtons = (subRowCol.elements || []).filter((webBannerButton) => webBannerButton.type === 'button' || webBannerButton.type === 'image' || webBannerButton.type === 'html');
              webBannerButtons = webBannerButtons.concat(subButtons);
            });
          });
        });

        const clickedButton = webBannerButtons.find((webBannerButton) => webBannerButton.id === elementId);

        const webBannerButtonActions = (clickedButton || {}).webBannerButtonActions || [];
        webBannerButtonActions.forEach((action, index) => {
          const rowId = `[id*=row-${action.row}]`;
          const elementIdSelector = `[id*=element-${action.element}]`;
          if (action.type === 'showRow' && action.row && clickedButton.url !== '#close') {
            showElement(rowId);
          } else if (action.type === 'hideRow' && action.row && clickedButton.url !== '#close') {
            hideElement(rowId);
          } else if (action.type === 'showElement' && action.element && clickedButton.url !== '#close') {
            showElement(elementIdSelector);
          } else if (action.type === 'hideElement' && action.element && clickedButton.url !== '#close') {
            hideElement(elementIdSelector);
          } else if (action.type === 'function' && action.function) {
            try {
              eval(action.function);
            } catch (err) {
              console.error(err);
            }
            if (index === webBannerButtonActions.length - 1) {
              closeBanners();
            }
          } else {
            closeBanners();
          }
        });

        if (e.currentTarget && e.currentTarget.type === 'submit' && e.currentTarget.form && !e.currentTarget.form.checkValidity()) {
          return;
        }

        if (clickedButton && !clickedButton.dontCountClickStatistics && !clickedElementIds[elementId] && !isPreview) {
          this.api.trackWebBannerEvent('clicked', bannerId, elementId, testId);
          clickedElementIds[elementId] = true;
        }

        const currentSlide = document.querySelector('.current-slide');

        if (currentSlide) {
          const href = bannerButtons[i].getAttribute('href');
          if (bannerButtons[i].classList.contains('next') || href === '#next-screen' || bannerButtons[i].classList.contains('carousel-indicator') || bannerButtons[i].classList.contains('prev') || href === '#prev-screen') {
            requestAnimationFrame(() => {
              const carousel = document.querySelector('.carousel');
              const height = `${carousel.offsetHeight}px`;
              this.setIframeHeight(height);
            });
          }
        } else {
          this.setIframeHeight();
        }
      }, true);
    }
    if (this.webBanner.carouselEnabled) {
      createCarousel(document, this.iframe, this.webBanner.carouselAutoPlayInterval);
    }
  }

  hide() {
    this.isVisible = false;
    this.element.style.display = 'none';
    document.body.classList.remove(WEB_BANNER_OPEN_CLOSE_CSS_CLASS);
  }

  show() {
    if (this.context.hasOutbrainWidget && isOutbrainWidgetInViewport(OUTBRAIN_CSS_SELECTOR)) {
      log.info('Outbrain widget is in viewport, not showing web banner');
      return;
    }

    this.isVisible = true;
    this.element = document.createElement('div');
    this.element.className = 'cleverpush-banner';
    this.element.id = `cleverpush-banner-${this.webBanner._id}`;
    this.element.innerHTML = (`
      <div class="banner-content">
        <button type="button" class="banner-close">&times;</button>
        <iframe frameborder="0" scrolling="no" allowtransparency></iframe>
      </div>
    `);

    let appendElement = document.body;
    if (this.webBanner.appendToSelector) {
      appendElement = document.querySelector(this.webBanner.appendToSelector);
    } else if (this.webBanner.darkenBackground) {
      document.body.classList.add(WEB_BANNER_OPEN_CLOSE_CSS_CLASS);
    }

    if (this.webBanner.customCss) {
      const style = document.createElement('style');
      style.innerHTML = this.webBanner.customCss;
      document.head.appendChild(style);
    }

    try {
      const bannerId = this.webBanner._id;
      const testId = this.webBanner.testId || null;

      let shownWebBanners = {};
      const storedData = sessionStorage.getItem('cleverpush-shown-web-banners');
      if (storedData) {
        shownWebBanners = JSON.parse(storedData);
      }

      shownWebBanners[bannerId] = testId;

      sessionStorage.setItem('cleverpush-shown-web-banners', JSON.stringify(shownWebBanners));
    } catch (e) {
      console.error('Error saving shown web banners:', e);
    }

    if (appendElement) {
      appendElement.appendChild(this.element);

      this.content = this.element.querySelector('.banner-content');
      if (this.content) {
        let bannerHtml = this.webBanner.content || '';
        this.iframe = this.content.querySelector('iframe');
        const htmlContainsScript = bannerHtml.includes('<script');
        if (this.iframe) {
          bannerHtml += `
          <style>
            ${htmlContainsScript ? '' : `
              body, body > table {
                background-color: transparent !important;
              }
              html, body {
                height: fit-content !important;
              }
            `}
            ${this.webBanner.customCss || ''}
            ${this.webBanner.carouselEnabled ? carouselStyle : ''}
          </style>
          `;

          this.iframe.contentWindow.document.write(bannerHtml);
          this.iframe.contentWindow.document.close();
          this.setupIframe(this.iframe.contentWindow);

          let iframeHeightSet = 0;
          const setIframeHeightInitial = () => {
            window.requestAnimationFrame(() => {
              this.setIframeHeight();
              iframeHeightSet += 1;
              if (iframeHeightSet < 300) { // 30 secs
                setTimeout(setIframeHeightInitial, 100);
              }
            });
          };
          setIframeHeightInitial();
        }
      }

      const hideElement = (id) => {
        const element = this.iframe.contentWindow.document.querySelector(id);
        if (element) {
          element.style.display = 'none';
        }
      };

      (this.webBanner?.contentState?.rows || []).forEach((row) => {
        (row.cols || []).forEach((col) => {
          const buttons = (col.elements || []).filter((webBannerButton) => webBannerButton.type === TYPE_BUTTON);
          buttons.forEach((button) => {
            const webBannerButtonActions = button.webBannerButtonActions || [];
            webBannerButtonActions.forEach((action) => {
              if (action.type === WEB_BANNER_BUTTON_ACTIONS.SHOW_ROW && action.row && button.url !== CLOSE_BANNER_BUTTON_ACTION) {
                hideElement(`[${ROW_ID_PREFIX}-${action.row}]`);
              } else if (action.type === WEB_BANNER_BUTTON_ACTIONS.SHOW_ELEMENT && action.element && button.url !== CLOSE_BANNER_BUTTON_ACTION) {
                hideElement(`[${ELEMENT_ID_PREFIX}-${action.element}]`);
              }
            });
          });
        });
      });

      if (this.webBanner.darkenBackground) {
        this.element.style.backgroundColor = 'rgba(0,0,0,.75)';
      } else {
        this.element.style.pointerEvents = 'none';
        this.content.style.pointerEvents = 'initial';
      }

      this.content.style.backgroundColor = 'transparent';

      if (typeof this.webBanner.closeOnBackgroundClick === 'undefined' || this.webBanner.closeOnBackgroundClick) {
        this.element.addEventListener('click', (e) => {
          if (e.target === e.currentTarget) {
            this.element.style.display = 'none';
            document.body.classList.remove(WEB_BANNER_OPEN_CLOSE_CSS_CLASS);

            if (this.webBanner._id && this.config.env !== 'PREVIEW') {
              this.api.trackWebBannerEvent(TRACK_EVENTS.CLOSED, this.webBanner._id, '', this.testId);
            }

            this.removeBounceListener();
          }
        });
      }

      if (typeof this.webBanner.style === 'object') {
        if (this.webBanner.style.align === 'top') {
          this.content.style.top = 0;
        }
        if (this.webBanner.style.align === 'bottom') {
          this.content.style.bottom = 0;
        }
        if (this.webBanner.style.align === 'left') {
          this.content.style.left = 0;
        }
        if (this.webBanner.style.align === 'right') {
          this.content.style.right = 0;
        }

        if (this.webBanner.style.align === 'centered') {
          this.content.style.top = '50%';
          this.content.style.left = '50%';
          this.content.style.transform = 'translate(-50%, -50%)';
        }

        if (this.webBanner.style.align === 'bottom-center') {
          this.content.style.left = '50%';
          this.content.style.bottom = '5px';
          this.content.style.transform = 'translate(-50%)';
        }

        if (this.webBanner.style.align === 'top') {
          delete this.webBanner.style.bottom;

          if (this.webBanner.style.top) {
            this.content.style.maxHeight = `calc(100vh - ${this.webBanner.style.top}px)`;
          }
        }
        if (this.webBanner.style.align === 'bottom') {
          delete this.webBanner.style.top;

          if (this.webBanner.style.bottom) {
            this.content.style.maxHeight = `calc(100vh - ${this.webBanner.style.bottom}px)`;
          }
        }
        if (this.webBanner.style.align === 'left') {
          delete this.webBanner.style.right;
        }
        if (this.webBanner.style.align === 'right') {
          delete this.webBanner.style.left;
        }

        if (this.webBanner.style.backgroundImage && !this.webBanner.style.backgroundPosition) {
          this.webBanner.style.backgroundPosition = 'center';
        }
        if (this.webBanner.style.backgroundImage && !this.webBanner.style.backgroundRepeat) {
          this.webBanner.style.backgroundRepeat = 'no-repeat';
        }
        if (this.webBanner.style.backgroundImage && !this.webBanner.style.backgroundSize) {
          this.webBanner.style.backgroundSize = 'cover';
        }
        if (this.webBanner.style.backgroundImage && !this.webBanner.style.backgroundAttachment) {
          this.webBanner.style.backgroundAttachment = 'local';
        }
        if (this.webBanner.style.zIndex) {
          this.element.style.zIndex = this.webBanner.style.zIndex;
        }

        // eslint-disable-next-line no-restricted-syntax
        for (const property in this.webBanner.style) {
          // eslint-disable-next-line no-prototype-builtins
          if (this.webBanner.style.hasOwnProperty(property)) {
            let prop = this.webBanner.style[property];
            if (property === 'backgroundImage' && prop.indexOf('url(') < 0) {
              prop = `url(${prop})`;
            }
            this.content.style[property] = typeof prop === 'number' ? (`${prop}px`) : prop;
          }
        }

        if (!this.webBanner.style.align || this.webBanner.style.align === 'center') {
          this.content.style.margin = '15px auto 0';
          this.content.style.maxHeight = 'calc(100% - 30px)';
        }

        if (this.webBanner.style.fullWidth) {
          this.content.style.width = '100%';
        } else if (!this.webBanner.style.align || this.webBanner.style.align === 'center') {
          this.content.style.left = '50%';
          this.content.style.width = `${this.contentWidth}px`;
          this.content.style.marginLeft = `${-1 * (this.contentWidth / 2)}px`;
        } else {
          this.content.style.width = `${this.contentWidth}px`;
        }
      }

      if (this.webBanner.linkEnabled && this.webBanner.link) {
        this.content.style.cursor = 'pointer';
        this.iframe.style.pointerEvents = 'none';
        this.content.addEventListener('click', () => {
          if (this.webBanner.linkTarget === '_top') {
            top.location.href = this.webBanner.link;
          } else {
            window.open(this.webBanner.link, '_blank');
          }
          if (this.webBanner._id && this.config.env !== 'PREVIEW') {
            this.api.trackWebBannerEvent(TRACK_EVENTS.CLICKED, this.webBanner._id, '', this.testId);
            this.trigger(Event.BANNER_CLICKED, {
              bannerId: this.webBanner._id,
              testId: this.testId,
            });
          }
        });
      }

      this.closeButton = this.element.querySelector('.banner-close');
      if (this.closeButton) {
        this.closeButton.addEventListener('click', this.onCloseClick.bind(this));

        if (this.webBanner.style && this.webBanner.style.color) {
          this.closeButton.style.color = this.webBanner.style.color;
        }
      }

      if (this.webBanner._id && this.config.env !== 'PREVIEW') {
        this.api.trackWebBannerEvent(TRACK_EVENTS.DELIVERED, this.webBanner._id, '', this.testId);
        this.addBounceListener();
      }
    }

    if (this.webBanner.autoCloseDurationEnabled && this.config.env !== 'PREVIEW' && !location.search?.includes('cleverPushWebBannerId=')) {
      this.autoCloseWebBannerTimer = setTimeout(() => this.hide(), this.webBanner.autoCloseDuration * 1000);
    }
  }

  addBounceListener() {
    if (typeof navigator.sendBeacon !== 'function' || this.bounceListener) {
      return;
    }

    this.bounceListener = () => {
      const blob = new Blob([JSON.stringify({ bannerId: this.webBanner._id, testId: this.testId })], { type: 'application/x-www-form-urlencoded' });
      navigator.sendBeacon(`${this.api.endpoint}/web-banner/event/${TRACK_EVENTS.BOUNCED}`, blob);
    };
    window.addEventListener('pagehide', this.bounceListener);
  }

  removeBounceListener() {
    if (!this.bounceListener) {
      return;
    }

    window.removeEventListener('pagehide', this.bounceListener);
    this.bounceListener = null;
  }

  onClick(e) {
    e.preventDefault();
    if (typeof this.onClickCallback === 'function') {
      this.onClickCallback();
    }

    if (this.webBanner._id && this.config.env !== 'PREVIEW') {
      this.api.trackWebBannerEvent(TRACK_EVENTS.CLICKED, this.webBanner._id, '', this.testId);
      this.trigger(Event.BANNER_CLICKED, {
        bannerId: this.webBanner._id,
        testId: this.testId,
      });
      this.removeBounceListener();
    }

    this.element.style.display = 'none';
    document.body.classList.remove(WEB_BANNER_OPEN_CLOSE_CSS_CLASS);

    if (this.webBanner.buttonUrl) {
      try {
        const popup = window.open(this.webBanner.buttonUrl, '_blank');
        if (!popup) {
          location.href = this.webBanner.buttonUrl;
        }
      } catch (ignored) {
        location.href = this.webBanner.buttonUrl;
      }
    }
  }

  saveBannerClick = (bannerId) => {
    let bannerIds = [];
    if (localStorage.getItem(CLICK_BANNER_LOCAL_STORAGE_KEY)) {
      bannerIds = JSON.parse(localStorage.getItem(CLICK_BANNER_LOCAL_STORAGE_KEY));

      if (bannerIds.indexOf(bannerId) === -1) {
        bannerIds.push(bannerId);
      }
    } else {
      bannerIds.push(bannerId);
    }
    localStorage.setItem(CLICK_BANNER_LOCAL_STORAGE_KEY, JSON.stringify(bannerIds));
  }

  onCloseClick(e) {
    e.preventDefault();
    e.stopPropagation();

    if (typeof this.onClickCallback === 'function') {
      this.onClickCallback();
    }

    this.isVisible = false;
    this.element.style.display = 'none';

    if (this.autoCloseWebBannerTimer) {
      clearTimeout(this.autoCloseWebBannerTimer);
    }

    document.body.classList.remove(WEB_BANNER_OPEN_CLOSE_CSS_CLASS);

    if (this.webBanner._id && this.config.env !== 'PREVIEW') {
      this.api.trackWebBannerEvent(TRACK_EVENTS.CLOSED, this.webBanner._id, '', this.testId);
      this.removeBounceListener();
    }

    if (this.webBanner.oncePerClick) {
      this.saveBannerClick(this.webBanner._id);
    }
  }
}
